import { z } from "zod";

const comparisonOperatorSchema = z.enum(["eq", "gte", "lte"]);

const ratingFilterSchema = z.object({
    value: z.enum(["1", "2", "3", "4", "5"]),
    operator: comparisonOperatorSchema.default("eq"),
});

export type RatingFilter = z.infer<typeof ratingFilterSchema>;

const arrayOrScalar = <T extends z.ZodTypeAny>(schema: T) =>
    z
        .union([z.array(schema), schema])
        .transform((value) => (Array.isArray(value) ? value : [value]))
        .optional();

const SearchSortSchema = z.enum([
    "best-match",
    "nearest-first",
    "newest-listings",
    "oldest-listings",
    "most-popular",
    "cutest-first",
]);

export type SearchSortValue = z.infer<typeof SearchSortSchema>;

const BestMatchPreferencesSchema = z.object({
    hypoallergenic: z.boolean().optional(),
    goodWithKids: z.boolean().optional(),
    goodWithDogs: z.boolean().optional(),
    goodWithCats: z.boolean().optional(),
    easyToTrain: z.boolean().optional(),
    lowShedding: z.boolean().optional(),
    active: z.boolean().optional(),
    lowerHealthRisk: z.boolean().optional(),
});

export type BestMatchPreferences = z.infer<typeof BestMatchPreferencesSchema>;

const BaseFiltersSchema = z.object({
    q: z.coerce.string().trim().min(1).max(500).optional(),
    lat: z.number().optional(),
    lng: z.number().optional(),
    maxDistanceMiles: z.number().positive().optional(),
    hardRadius: z.boolean().optional(),

    breedSlugs: arrayOrScalar(z.coerce.string())
        .transform((slugs) => {
            const cleaned = slugs?.filter((slug) => slug.trim().length > 0);
            return cleaned && cleaned.length > 0 ? cleaned : undefined;
        })

        .optional(),
    dogName: z.coerce.string().optional(),
    breedType: z.enum(["purebred", "mixed"]).optional(),
    providerName: z.coerce.string().optional(),
    sex: z.enum(["male", "female"]).optional(),
    priceMin: z.number().int().nonnegative().optional(),
    priceMax: z.number().int().nonnegative().optional(),
    dobMin: z.number().int().optional(),
    dobMax: z.number().int().optional(),
    size: arrayOrScalar(z.enum(["toy", "small", "medium", "large", "giant"])),
    coatType: arrayOrScalar(
        z.enum([
            "smooth",
            "short",
            "medium",
            "long",
            "wire",
            "curly",
            "double",
            "hairless",
        ]),
    ),
    sheddingLevel: arrayOrScalar(
        z.enum(["none", "minimal", "moderate", "heavy", "seasonal_blowout"]),
    ),
    hypoallergenic: z.boolean().optional(),
    friendlinessChildren: arrayOrScalar(ratingFilterSchema),
    friendlinessDogs: arrayOrScalar(ratingFilterSchema),
    friendlinessCats: arrayOrScalar(ratingFilterSchema),
    energyLevel: arrayOrScalar(ratingFilterSchema),
    trainability: arrayOrScalar(ratingFilterSchema),
    healthIssuesRisk: arrayOrScalar(ratingFilterSchema),
    page: z.number().optional(),
    limit: z.number().optional(),
});

export type BaseFilters = z.infer<typeof BaseFiltersSchema>;

const ProviderTypeSchema = z.enum(["breeder", "shelter_rescue"]);

export type ProviderTypeFilter = z.infer<typeof ProviderTypeSchema>;

export const UrlSearchParamsSchema = BaseFiltersSchema.extend({
    locationSource: z.literal("estimated").optional(),
    sortBy: SearchSortSchema.optional(),
    bestMatchPreferences: BestMatchPreferencesSchema.optional(),
    feedContext: z.coerce.string().optional(),
    providerType: ProviderTypeSchema.optional(),

    quiz_v: z.coerce.string().optional(),
    quiz_trainable: z.coerce.string().optional(),
    quiz_energy: z.coerce.string().optional(),
    quiz_size: z.coerce.string().optional(),
    quiz_shedding: z.coerce.string().optional(),
    quiz_family: z.coerce.string().optional(),
    quiz_grooming: z.coerce.string().optional(),

    pickerChosen: z.coerce.string().optional(),
});

export type UrlSearchParams = z.infer<typeof UrlSearchParamsSchema>;
